import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Alert, ListSubheader,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const SessionSelector = ({ sessions, currentSession, onSessionChange }) => {
  const { currentSite, isSuperAdmin } = useAuth();
  const [timeoutWarning, setTimeoutWarning] = useState(false);

  useEffect(() => {
    const checkTimeout = () => {
      const lastActivity = localStorage.getItem('last_activity');
      if (lastActivity) {
        const elapsed = (Date.now() - parseInt(lastActivity)) / 1000;
        if (elapsed > 840) {
          setTimeoutWarning(true);
        }
      }
    };

    const interval = setInterval(checkTimeout, 60000);
    checkTimeout();

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem('last_activity', Date.now().toString());
      setTimeoutWarning(false);
    };

    window.addEventListener('mousedown', updateActivity);
    window.addEventListener('keypress', updateActivity);

    return () => {
      window.removeEventListener('mousedown', updateActivity);
      window.removeEventListener('keypress', updateActivity);
    };
  }, []);

  const filteredSessions = useMemo(() => {
    if (!currentSite) return sessions;
    return sessions.filter(s => s.site === currentSite.id || s.site_id === currentSite.id);
  }, [sessions, currentSite]);

  const showGrouped = isSuperAdmin && !currentSite && sessions.length > 0;

  const groupedBySite = useMemo(() => {
    if (!showGrouped) return null;
    const groups = {};
    for (const session of sessions) {
      const siteName = session.site_name || 'Unknown Site';
      if (!groups[siteName]) groups[siteName] = [];
      groups[siteName].push(session);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [sessions, showGrouped]);

  const sessionLabel = (session) => session.display_name || session.name || 'Session';

  const renderMenuItems = () => {
    if (showGrouped && groupedBySite) {
      return groupedBySite.flatMap(([siteName, siteSessions]) => [
        <ListSubheader key={`header-${siteName}`} sx={{ fontWeight: 700, color: 'primary.main', lineHeight: '32px' }}>
          {siteName}
        </ListSubheader>,
        ...siteSessions.map(session => (
          <MenuItem key={session.id} value={String(session.id)} sx={{ pl: 4 }}>
            {sessionLabel(session)}
          </MenuItem>
        )),
      ]);
    }

    return filteredSessions.map(session => (
      <MenuItem key={session.id} value={String(session.id)}>
        {sessionLabel(session)}
      </MenuItem>
    ));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Current Session
        {currentSite && (
          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            — {currentSite.name}
          </Typography>
        )}
      </Typography>

      {timeoutWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session will expire soon. Please save your work.
        </Alert>
      )}

      <FormControl fullWidth size="small" sx={{ minWidth: 280 }}>
        <InputLabel id="session-select-label">Select Session</InputLabel>
        <Select
          labelId="session-select-label"
          value={currentSession?.id != null ? String(currentSession.id) : ''}
          label="Select Session"
          onChange={(e) => onSessionChange(e.target.value)}
          displayEmpty
          notched
        >
          <MenuItem value="">
            <em>Select a session</em>
          </MenuItem>
          {renderMenuItems()}
        </Select>
      </FormControl>

      {currentSession && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Students: {currentSession.student_count || 0} | Employees:{' '}
            {currentSession.employee_count || 0}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default SessionSelector;
